import React, { useState, useEffect, useRef } from "react";
import { Clock, Check, ArrowLeft, Users } from "feather-icons-react/build/IconComponents";
import { base_api_url } from "../../../environment.jsx";
import { BiRestaurant } from "react-icons/bi";
import axios from "axios";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import PropTypes from "prop-types";
import CuisineAuth from "../../components/SessionControl/cuisine-auth";
import CuisineHeader from "../../components/CuisineHeader/cuisineheader";
import SessionCuisinier from "../../components/SessionControl/sessionCuisinier";
import VerifyRestaurant from "../../components/SessionControl/checkrestau";

const Cuisine = () => {
  const MySwal = withReactContent(Swal);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [cuisinier, setCuisinier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [devise, setDevise] = useState('FCFA');
  const [lastRefresh, setLastRefresh] = useState(null);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);

  // État pour la vue mobile
  const [isMobileDetailView, setIsMobileDetailView] = useState(false);
  
  // NOUVEAU: État local pour forcer le re-render quand l'étiquette change
  const [selectedEtiquette, setSelectedEtiquette] = useState(localStorage.getItem('selectedEtiquette') || "");

  // Référence pour l'intervalle d'actualisation automatique
  const autoRefreshInterval = useRef(null);

  // Référence pour éviter les appels multiples
  const isRefreshing = useRef(false);

  // Vérifier l'authentification au montage
  useEffect(() => {
    const checkAuth = () => {
      const cuisinierRaw = localStorage.getItem("cuisinier_actif");
      if (cuisinierRaw) {
        try {
          const cuisinierData = JSON.parse(cuisinierRaw);
          if (cuisinierData && cuisinierData.access_token) {
            setCuisinier(cuisinierData);
            setIsAuthenticated(true);
          }
        } catch (error) {
          console.error("Erreur parsing cuisinier_actif:", error);
          localStorage.removeItem("cuisinier_actif");
          setIsAuthenticated(false);
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  // Fonction pour démarrer l'actualisation automatique
  const startAutoRefresh = () => {
    if (autoRefreshInterval.current) {
      clearInterval(autoRefreshInterval.current);
    }

    autoRefreshInterval.current = setInterval(() => {
      if (autoRefreshEnabled && isAuthenticated && cuisinier && !isRefreshing.current) {
        console.log("🔄 Actualisation automatique des commandes...");
        fetchCommands(true); // true = actualisation silencieuse
      }
    }, 120000); // 120 secondes = 2 minutes
  };

  // Fonction pour arrêter l'actualisation automatique
  const stopAutoRefresh = () => {
    if (autoRefreshInterval.current) {
      clearInterval(autoRefreshInterval.current);
      autoRefreshInterval.current = null;
    }
  };

  // Callback quand la session expire
  const handleSessionExpired = () => {
    stopAutoRefresh();
    setIsAuthenticated(false);
    setCuisinier(null);
    setLoading(false);
  };

  // Fonction de déconnexion
  const handleLogout = () => {
    MySwal.fire({
      // title: "Déconnexion",
      html: '<div style="text-align: center;">Êtes-vous sûr de vouloir vous déconnecter ?</div>',
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Oui, se déconnecter",
      cancelButtonText: "Annuler"
    }).then((result) => {
      if (result.isConfirmed) {
        stopAutoRefresh();
        localStorage.removeItem("cuisinier_actif");
        setIsAuthenticated(false);
        setCuisinier(null);

        MySwal.fire({
          icon: "success",
          // title: "Déconnexion réussie",
          text: "Vous avez été déconnecté avec succès.",
          toast: true,
          position: "bottom-end",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          customClass: {
            popup: "pixel-toast",
          }
        });
      }
    });
  };

  // Ordre de priorité personnalisé pour l'affichage en cuisine
  const getStatusPriority = (order) => {
    // Statut 3 ou 'en_preparation' = priorité 1 (en premier)
    if (order.statutNumeric === 3 || order.statut === 'en_preparation') {
      return 1;
    }
    // Statut 4 ou 'prete' = priorité 2 (en second)
    if (order.statutNumeric === 4 || order.statut === 'prete') {
      return 2;
    }
    // Autres statuts = priorité 3
    return 3;
  };

  const fetchCommands = async (silent = false) => {
    if (!cuisinier?.access_token || !cuisinier?.id_restaurant) {
      console.error("Token ou ID restaurant manquant");
      return;
    }

    // Éviter les appels multiples simultanés
    if (isRefreshing.current) {
      return;
    }

    isRefreshing.current = true;

    if (!silent) {
      setLoadingOrders(true);
    }

    try {
      const token = cuisinier.access_token;
      const idRestaurant = cuisinier.id_restaurant;

      const response = await axios.get(`${base_api_url}api/commande/restaurant/${idRestaurant}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.status === 200 || response.status === 201) {
        // Récupérer les infos du restaurant pour la devise
        try {
          const restoResponse = await axios.get(`${base_api_url}api/restaurant/${idRestaurant}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setDevise(restoResponse.data.restaurant?.devise || restoResponse.data.Restaurant?.devise || "FCFA");
        } catch (error) {
          console.error("Erreur récupération restaurant:", error);
        }

        const commandsData = (response.data.commandes || response.data || []).map(cmd => ({
          id: cmd.id_commande,
          reference: cmd.reference,
          statut: cmd.statut == "2" ? "en_attente" : cmd.statut == "3" ? "en_preparation" : cmd.statut == "4" ? "prete" : cmd.statut,
          statutNumeric: parseInt(cmd.statut),
          created_at: cmd.date_commande,
          table: cmd.numtable || cmd.numtable,
          client_nom: cmd.nom || "",
          total: cmd.prix,
          notes: cmd.notes || "",
          details: (cmd.details || []).map(detail => ({
            id: detail.id_details,
            article_nom: detail.article?.nom || "",
            etiquette: detail.article?.etiquette || "",
            etat: (detail?.etat || detail?.statut) == 1 ? "en_attente" : (detail?.etat || detail?.statut) == 2 ? "en_preparation" : "pret",
            quantite: detail.quantite,
            prix_unitaire: detail.prix_unitaire,
            statut_article: detail.statut_article === true ? "en_attente" : detail.statut_article === "3" ? "en_preparation" : "Prêt",
            instructions: detail.commentaire || ""
          }))
        }));

        setOrders(commandsData
          .filter(cmd => cmd.statut == 3 || cmd.statut == 'en_preparation' || cmd.statutNumeric == 4 || cmd.statut == 'prete')
          .filter(cmd => new Date(cmd.created_at).toDateString() === new Date().toDateString()) // Comparaison de date complète
          .sort((a, b) => {
            const aPriority = getStatusPriority(a);
            const bPriority = getStatusPriority(b);

            // Trier par priorité de statut d'abord
            if (aPriority !== bPriority) {
              return aPriority - bPriority;
            }

            // Si même priorité de statut, trier par heure de création (plus ancien en premier)
            return new Date(a.created_at) - new Date(b.created_at);
          })
        );
        setLastRefresh(new Date());
        console.log("Commandes récupérées :", commandsData.filter(cmd => cmd.statut == 3 || cmd.statut == 'en_preparation' || cmd.statutNumeric == 4 || cmd.statut == 'prete'));

        if (!silent && commandsData.length <= 0) {
          MySwal.fire({
            icon: "info",
            // title: "Aucune commande",
            text: "Aucune commande trouvée pour ce restaurant.",
            toast: true,
            position: "bottom-end",
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
            customClass: {
              popup: "pixel-toast",
            },
          });
        }
      }
    } catch (error) {
      console.error("Error fetching commands:", error);

      // En mode silencieux, ne pas afficher d'erreur sauf si c'est critique
      if (!silent || error.response?.status === 401) {
        //         let errorTitle = "Erreur de chargement";
        let errorMessage = "Impossible de charger les commandes.";

        if (error.response?.status === 401) {
          //           errorTitle = "Session expirée";
          errorMessage = "Votre session a expiré. Veuillez vous reconnecter.";
          handleSessionExpired();
          return;
        }

        MySwal.fire({
          icon: "error",
          // title: errorTitle,
          text: errorMessage,
          toast: true,
          position: "bottom-end",
          showConfirmButton: false,
          timer: 5000,
          timerProgressBar: true,
          customClass: {
            popup: "pixel-toast",
          },
        });
      }

      if (!silent) {
        setOrders([]);
      }
    } finally {
      if (!silent) {
        setLoadingOrders(false);
      }
      isRefreshing.current = false;
    }
  };

  // Fonction pour sélectionner une commande
  const selectOrder = (order) => {
    setSelectedOrder(order);
    if (window.innerWidth < 768) { // Mobile
      setIsMobileDetailView(true);
    }
  };

  // Fonction pour revenir à la liste sur mobile
  const backToList = () => {
    setIsMobileDetailView(false);
    setSelectedOrder(null);
  };

  // Fonction pour obtenir le nombre total de plats
  const getTotalDishes = (order) => {
    return order.details.reduce((total, detail) => total + detail.quantite, 0);
  };

  // Composant pour une commande dans la liste (version simplifiée)
  const OrderListItem = ({ order, isSelected }) => {
    const totalDishes = getTotalDishes(order);

    return (
      <div
        className={`card mb-2 cursor-pointer ${isSelected ? 'border-primary' : ''}`}
        onClick={() => selectOrder(order)}
        style={{
          cursor: 'pointer',
          backgroundColor: isSelected ? '#f8f9ff' : 'white',
          borderWidth: isSelected ? '2px' : '1px'
        }}
      >
        <div className="card-body p-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="mb-0 fw-bold">#{order.reference}</h6>
            <span
              className="badge rounded-pill px-2"
              style={{ backgroundColor: getStatusColor(order.statut), color: '#fff' }}
            >
              {getStatusText(order.statut) == 10 ? 'En attente' : getStatusText(order.statut)}
            </span>
          </div>

          <div className="d-flex justify-content-between align-items-center mb-2">
            <div>
              <small className="text-muted d-flex align-items-center">
                <Users size={12} className="me-1" />
                {totalDishes} plat{totalDishes > 1 ? 's' : ''}
              </small>
              <small className="text-muted">{order.table}</small>
            </div>
            <small className="text-muted d-flex align-items-center">
              <Clock size={12} className="me-1" />
              {new Date(order.created_at).toLocaleTimeString('fr-FR', {
                hour: '2-digit',
                minute: '2-digit'
              })}
            </small>
          </div>

          {order.statut === "en_preparatio" && (
            <div className="mt-2">
              <div className="progress" style={{ height: '3px' }}>
                <div
                  className="progress-bar bg-success"
                  style={{ width: `${(order.details.filter(d => d.etat === "pret").length / order.details.length) * 100}%` }}
                ></div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Validation PropTypes pour OrderListItem
  OrderListItem.propTypes = {
    order: PropTypes.shape({
      id: PropTypes.number.isRequired,
      reference: PropTypes.string.isRequired,
      statut: PropTypes.string.isRequired,
      created_at: PropTypes.string.isRequired,
      table: PropTypes.string.isRequired,
      client_nom: PropTypes.string.isRequired,
      total: PropTypes.number.isRequired,
      notes: PropTypes.string,
      details: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number.isRequired,
          article_nom: PropTypes.string.isRequired,
          etat: PropTypes.string.isRequired,
          quantite: PropTypes.number.isRequired,
          prix_unitaire: PropTypes.number.isRequired,
          instructions: PropTypes.string
        })
      ).isRequired
    }).isRequired,
    isSelected: PropTypes.bool.isRequired
  };

  // Composant pour les détails de la commande (zone droite)
  const OrderDetails = ({ order }) => {
    if (!order) {
      return (
        <div className="d-flex flex-column align-items-center justify-content-center h-100 text-muted">
          <BiRestaurant size={64} className="mb-3" style={{ opacity: 0.3 }} />
          <h5>Sélectionnez une commande</h5>
          <p>Cliquez sur une commande dans la liste pour voir ses détails</p>
        </div>
      );
    }

    // const totalArticles = order.details.length;
    // const articlesReady = order.details.filter(d => d.etat === "pret").length;

    return (
      <div className="h-100 d-flex flex-column">
        {/* Header de la commande */}
        <div className="border-bottom pb-3 mb-3">
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <h4 className="mb-1">Commande #{order.reference}</h4>
              <p className="text-muted mb-2">{order.table} • {order.client_nom}</p>
              <div className="d-flex align-items-center gap-3">
                <span className="fw-bold">{order.total} {devise}</span>
                <small className="text-muted d-flex align-items-center">
                  <Clock size={14} className="me-1" />
                  {new Date(order.created_at).toLocaleTimeString('fr-FR', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </small>
              </div>
            </div>
            <span
              className="badge rounded-pill px-3 py-2"
            >
              {/* {getStatusText(order.statut) == 10 ? 'En attente' : getStatusText(order.statut)} */}
              {(order.statut == 3 || order.statut == 'en_preparation') && (
                <button
                  className="btn btn-success btn-lg px-4"
                  onClick={() => updateOrderStatus(order.reference, "prete", 4)}
                >
                  <Check size={16} className="me-2" />
                  Terminer
                </button>
              )}
            </span>
          </div>
        </div>

        {/* Notes de la commande */}
        {order.notes && (
          <div className="alert alert-info mb-3" style={{ backgroundColor: '#e3f2fd', borderColor: '#90caf9' }}>
            <strong style={{ color: '#1976d2' }}>📝 Note:</strong>
            <p className="mb-0 mt-2">{order.notes}</p>
          </div>
        )}

        {/* Liste des articles - Zone scrollable */}
        <div className="flex-grow-1 overflow-auto">
          <h6 className="mb-3">Articles de la commande:</h6>

          {order.details.map((article) => (
            <div
              key={article.id}
              className="border rounded mb-3 p-3"
              style={{
                backgroundColor: article.etat === "pret" ? '#f8f9fa' : '#fff',
                borderColor: article.etat === "pret" ? '#28a745' : article.etat === "en_preparation" ? '#007bff' : '#ffc107',
                borderWidth: '2px'
              }}
            >
              <div className="d-flex justify-content-between align-items-start">
                <div className="flex-grow-1">
                  <h6 className="mb-1" style={{
                    textDecoration: article.etat === "pret" ? 'line-through' : 'none',
                    opacity: article.etat === "pret" ? 0.7 : 1
                  }}>
                    {article.article_nom}
                  </h6>
                  <div className="text-muted mb-2">
                    <strong>Quantité:</strong> {article.quantite} •
                    <strong> Prix:</strong> {article.prix_unitaire} {devise}
                  </div>
                  {(article.instructions && article.instructions.trim() !== "") && (
                    <div className="alert alert-warning py-2 mb-0">
                      <small><strong>Instructions:</strong> {article.instructions}</small>
                    </div>
                  )}
                </div>

                {/* <div className="d-flex flex-column align-items-end gap-2 ms-3">
                  <span 
                    className="badge"
                    style={{ 
                      backgroundColor: getStatusColor(article.etat), 
                      color: '#fff',
                      fontSize: '0.9em',
                      padding: '8px 12px'
                    }}
                  >
                    {getStatusText(article.etat, order.statut)}
                  </span>
                  
                  {article.etat === "en_attente" && (
                    <button
                      className="btn btn-info btn-sm"
                      onClick={() => updateArticleStatus(order.id, article.id, 2)}
                    >
                      <Play size={14} className="me-1" />
                      Commencer
                    </button>
                  )}
                  
                  {article.etat === "en_preparation" && (
                    <button
                      className="btn btn-success btn-sm"
                      onClick={() => updateArticleStatus(order.id, article.id, 3)}
                    >
                      <Check size={14} className="me-1" />
                      Prêt
                    </button>
                  )}
                </div> */}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Validation PropTypes pour OrderDetails
  OrderDetails.propTypes = {
    order: PropTypes.shape({
      id: PropTypes.number.isRequired,
      reference: PropTypes.string.isRequired,
      statut: PropTypes.string.isRequired,
      created_at: PropTypes.string.isRequired,
      table: PropTypes.string.isRequired,
      client_nom: PropTypes.string.isRequired,
      total: PropTypes.number.isRequired,
      notes: PropTypes.string,
      details: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number.isRequired,
          article_nom: PropTypes.string.isRequired,
          etat: PropTypes.string.isRequired,
          quantite: PropTypes.number.isRequired,
          prix_unitaire: PropTypes.number.isRequired,
          instructions: PropTypes.string
        })
      ).isRequired
    })
  };

  // Fonction pour filtrer les commandes par statut et étiquette
  const getFilteredOrders = () => {
    // Si le cuisinier a une étiquette assignée (depuis restousers.jsx/le backend)
    const cuisinierEtiquette = cuisinier?.etiquette; 
    
    // Filtre manuel est maintenant géré par l'état React 'selectedEtiquette'
    const activeFilter = cuisinierEtiquette || selectedEtiquette;

    const filterOrderByEtiquette = (order, etiquetteToFilter) => {
      // S'il n'y a aucun filtre actif, on affiche toute la commande
      if (!etiquetteToFilter) return order;

      const matchingDetails = order.details?.filter(detail =>
      {
        return detail.etiquette === etiquetteToFilter
      } 
      ) || [];

      // Si la commande ne contient aucun article pôle correspondant, on la retire
      if (matchingDetails.length === 0) return null;

      // On retourne la commande mais on ne montre QUE les articles du pôle correspondant
      return {
        ...order,
        details: matchingDetails
      };
    };

    return orders
      .map(order => filterOrderByEtiquette(order, activeFilter))
      .filter(order => order !== null)
      .filter(order => order.statut == 3 || order.statut == 'en_preparation' || order.statutNumeric == 4 || order.statut == 'prete')
      .sort((a, b) => {
        const aPriority = getStatusPriority(a);
        const bPriority = getStatusPriority(b);

        // Trier par priorité de statut d'abord
        if (aPriority !== bPriority) {
          return aPriority - bPriority;
        }

        // Si même priorité de statut, trier par heure de création (plus ancien en premier)
        return new Date(a.created_at) - new Date(b.created_at);
      })
  };

  const updateOrderStatus = async (reference, newStatus, statut) => {
    if (!cuisinier?.access_token) {
      console.error("Token manquant");
      return;
    }

    try {
      const token = cuisinier.access_token;

      const response = await axios.put(
        `${base_api_url}api/commande/${reference}`,
        { statut: statut },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.status === 200 || response.status === 201) {
        MySwal.fire({
          icon: "success",
          // title: "Statut mis à jour !",
          text: `Commande ${newStatus}`,
          toast: true,
          position: "bottom-end",
          showConfirmButton: false,
          timer: 2000,
          timerProgressBar: true,
          customClass: {
            popup: "pixel-toast",
          },
        });

        // Mettre à jour localement l'état de la commande
        setOrders(prevOrders =>
          prevOrders.map(order =>
            order.reference === reference
              ? {
                ...order,
                statut: statut === 3 ? "en_preparation" : statut === 4 ? "prete" : order.statut,
                statutNumeric: statut
              }
              : order
          )
        );

        // Vider l'espace de droite après la mise à jour réussie
        setSelectedOrder(null);

        // Si on est sur mobile, retourner à la liste
        if (window.innerWidth < 768) {
          setIsMobileDetailView(false);
        }

      }
    } catch (error) {
      console.error("Error updating command status:", error);

      if (error.response?.status === 401) {
        handleSessionExpired();
        return;
      }

      MySwal.fire({
        icon: "error",
        // title: "Erreur de mise à jour",
        text: "Impossible de mettre à jour le statut de la commande.",
        toast: true,
        position: "bottom-end",
        showConfirmButton: false,
        timer: 5000,
        timerProgressBar: true,
        customClass: {
          popup: "pixel-toast",
        },
      });
    }
  };

  // const updateArticleStatus = async (orderId, articleId, newStatus) => {
  //   console.log('status', newStatus)

  //   // Mise à jour du backend
  //   const token = cuisinier.access_token;
  //   const res = await axios.put(`${base_api_url}api/detailscmd/${articleId}`, {
  //       etat: newStatus
  //   }, {
  //       headers: { Authorization: `Bearer ${token}` }
  //   });

  //   if (res.status === 200 || res.status === 201) {
  //     console.log("Article status updated successfully");
  //     setOrders(prevOrders => 
  //       prevOrders.map(order => 
  //           order.id === orderId 
  //           ? {
  //               ...order,
  //               details: order.details.map(detail => 
  //                   detail.id === articleId 
  //                   ? { ...detail, etat: newStatus === 2 ? "en_preparation" : newStatus === 3 ? "pret" : "en_attente"}
  //                   : detail
  //               )
  //               }
  //           : order
  //       )
  //     );

  //     // Mettre à jour selectedOrder si c'est la commande en cours
  //     if (selectedOrder && selectedOrder.id === orderId) {
  //       setSelectedOrder(prev => ({
  //         ...prev,
  //         details: prev.details.map(detail => 
  //           detail.id === articleId 
  //           ? { ...detail, etat: newStatus === 2 ? "en_preparation" : newStatus === 3 ? "pret" : "en_attente"}
  //           : detail
  //         )
  //       }));
  //     }

  //     MySwal.fire({
  //       icon: "success",
  //       title: "Article mis à jour !",
  //       text: `Article ${newStatus === 2 ? "En Préparation" : newStatus === 3 ? "Prêt" : "En Attente"}`,
  //       toast: true,
  //       position: "bottom-end",
  //       showConfirmButton: false,
  //       timer: 1500,
  //       timerProgressBar: true,
  //       customClass: {
  //           popup: "pixel-toast",
  //       },
  //       });
  //   } else {
  //       console.error("Failed to update article status");
  //       MySwal.fire({
  //           icon: "error",
  //           title: "Erreur lors de la mise à jour de l'état.",
  //           toast: true,
  //           position: "bottom-end",
  //           showConfirmButton: false,
  //           timer: 1500,
  //           timerProgressBar: true,
  //           customClass: {
  //               popup: "pixel-toast",
  //           }
  //       });
  //   }
  // };

  // Fonction pour activer/désactiver l'actualisation automatique
  const toggleAutoRefresh = () => {
    setAutoRefreshEnabled(!autoRefreshEnabled);
    if (!autoRefreshEnabled) {
      startAutoRefresh();
      MySwal.fire({
        icon: "success",
        // title: "Actualisation automatique activée",
        text: "Les commandes seront actualisées toutes les 2 minutes.",
        toast: true,
        position: "bottom-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        customClass: {
          popup: "pixel-toast",
        },
      });
    } else {
      stopAutoRefresh();
      MySwal.fire({
        icon: "info",
        // title: "Actualisation automatique désactivée",
        text: "Vous devrez actualiser manuellement.",
        toast: true,
        position: "bottom-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        customClass: {
          popup: "pixel-toast",
        },
      });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "en_attente": return "#ffc107";
      case "en_preparation": return "#007bff";
      case "prete": return "#28a745";
      case "pret": return "#28a745";
      default: return "#6c757d";
    }
  };

  const getStatusText = (status, orderStatus) => {
    switch (status) {
      case "en_attente": return orderStatus == "en_preparation" ? "Attente de Préparation" : "En attente";
      case "en_preparation": return "En Cours";
      case "prete": return "Prête";
      case "pret": return "Prêt";
      default: return status;
    }
  };

  // Charger les commandes quand authentifié et démarrer l'actualisation auto
  useEffect(() => {
    if (isAuthenticated && cuisinier) {
      fetchCommands();
      if (autoRefreshEnabled) {
        startAutoRefresh();
      }
    }

    // Cleanup à la déconnexion ou au démontage du composant
    return () => {
      stopAutoRefresh();
    };
  }, [isAuthenticated, cuisinier]);

  // Gérer les changements de l'état autoRefreshEnabled
  useEffect(() => {
    if (isAuthenticated && cuisinier) {
      if (autoRefreshEnabled) {
        startAutoRefresh();
      } else {
        stopAutoRefresh();
      }
    }

    return () => {
      if (!autoRefreshEnabled) {
        stopAutoRefresh();
      }
    };
  }, [autoRefreshEnabled, isAuthenticated, cuisinier]);

  // Cleanup quand le composant est démonté
  useEffect(() => {
    return () => {
      stopAutoRefresh();
    };
  }, []);

  // Affichage du loader pendant la vérification d'auth
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Vérification...</span>
        </div>
      </div>
    );
  }

  // Si pas authentifié, afficher le modal d'auth
  if (!isAuthenticated) {
    return <CuisineAuth onAuthSuccess={() => window.location.reload()} />;
  }

  const filteredOrders = getFilteredOrders();

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
      {/* Contrôles existants */}
      <SessionCuisinier onSessionExpired={handleSessionExpired} />
      <VerifyRestaurant />
      <CuisineHeader
        onRefresh={() => fetchCommands(false)}
        loading={loadingOrders}
        onLogout={handleLogout}
        autoRefreshEnabled={autoRefreshEnabled}
        onToggleAutoRefresh={toggleAutoRefresh}
        lastRefresh={lastRefresh}
        selectedEtiquette={selectedEtiquette}
        onEtiquetteChange={(value) => {
          setSelectedEtiquette(value);
          localStorage.setItem('selectedEtiquette', value);
        }}
      />

      {loadingOrders ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Chargement...</span>
          </div>
        </div>
      ) : (
        <>
          {/* Version Desktop/Tablet - Deux colonnes */}
          <div className="d-none d-md-flex" style={{ height: 'calc(100vh - 80px)' }}>
            {/* Colonne gauche - Liste des commandes */}
            <div className="col-md-4 border-end bg-white p-3" style={{ overflowY: 'auto' }}>
              <h5 className="mb-3 d-flex justify-content-between align-items-center">
                Commandes
                <span className="badge bg-primary">{filteredOrders.length}</span>
              </h5>

              {filteredOrders.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <BiRestaurant size={48} className="mb-3" style={{ opacity: 0.3 }} />
                  <h6>Aucune commande</h6>
                  <p className="small">Les nouvelles commandes apparaîtront ici</p>
                </div>
              ) : (
                filteredOrders.map((order) => (
                  <OrderListItem
                    key={order.id}
                    order={order}
                    isSelected={selectedOrder?.id === order.id}
                  />
                ))
              )}
            </div>

            {/* Colonne droite - Détails de la commande */}
            <div className="col-md-8 bg-white p-4">
              <OrderDetails order={selectedOrder} />
            </div>
          </div>

          {/* Version Mobile */}
          <div className="d-md-none">
            {!isMobileDetailView ? (
              // Liste des commandes sur mobile
              <div className="p-3">
                <h5 className="mb-3 d-flex justify-content-between align-items-center">
                  Commandes
                  <span className="badge bg-primary">{filteredOrders.length}</span>
                </h5>

                {filteredOrders.length === 0 ? (
                  <div className="text-center py-5 text-muted">
                    <BiRestaurant size={48} className="mb-3" style={{ opacity: 0.3 }} />
                    <h6>Aucune commande</h6>
                    <p className="small">Les nouvelles commandes apparaîtront ici</p>
                  </div>
                ) : (
                  filteredOrders.map((order) => (
                    <OrderListItem
                      key={order.id}
                      order={order}
                      isSelected={false}
                    />
                  ))
                )}
              </div>
            ) : (
              // Détails de la commande sur mobile
              <div className="bg-white" style={{ minHeight: 'calc(100vh - 80px)' }}>
                <div className="p-3 border-bottom d-flex align-items-center">
                  <button
                    className="btn btn-outline-secondary btn-sm me-3"
                    onClick={backToList}
                  >
                    <ArrowLeft size={16} />
                  </button>
                  <h6 className="mb-0">Détails de la commande</h6>
                </div>
                <div className="p-3" style={{ height: 'calc(100vh - 140px)', overflow: 'auto' }}>
                  <OrderDetails order={selectedOrder} />
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Cuisine;